// src/analysis/sessionAnalysis/zoneDisplay/zoneDisplayBounds.ts
import type { Lap } from '../../../domain/lap/Lap'
import type { TrackZone } from '../../zones/TrackZoneTypes'

// ============================================================================
// Zone Boundary Refinement
// ============================================================================

/**
 * Refine zone boundaries for gradient display.
 *
 * For braking zones: extend to the midpoint of the next cornering zone.
 * Zone detection splits braking/cornering into separate zones, but the gradient
 * should show the full braking → turn-in → apex sequence as one visual unit.
 *
 * Uses the zone structure (allZones) rather than per-lap speed analysis.
 * This is stable regardless of outlier laps — one lap with very different
 * braking behavior can't corrupt the extension decision.
 */
export function refineZoneBoundaries(
  _domainLaps: Lap[],
  zone: TrackZone,
  _excludedLapIds: Set<string>,
  allZones?: TrackZone[]
): { startDistance: number; endDistance: number } {
  if (zone.type !== 'braking' || !allZones || allZones.length === 0) {
    return { startDistance: zone.startDistance, endDistance: zone.endDistance }
  }

  const zoneIndex = allZones.findIndex((z) => z.id === zone.id)
  if (zoneIndex < 0) {
    return { startDistance: zone.startDistance, endDistance: zone.endDistance }
  }

  for (let i = zoneIndex + 1; i < allZones.length; i++) {
    const candidate = allZones[i]
    if (candidate.type === 'braking') break
    if (candidate.type !== 'cornering') continue
    if (candidate.startDistance > zone.endDistance + 200) break
    return {
      startDistance: zone.startDistance,
      endDistance: Math.max(zone.endDistance, candidate.startDistance),
    }
  }

  return { startDistance: zone.startDistance, endDistance: zone.endDistance }
}

/**
 * Find the turn-in distance for a cornering zone by analyzing GPS heading
 * rate of change (curvature).
 *
 * Turn-in is where the rider starts steering into the corner — detectable as
 * the point where GPS heading begins changing consistently. We compute heading
 * rate from the reference lap's GPS points and walk forward from the approach
 * to find where curvature first exceeds a threshold relative to the
 * straight-line baseline.
 *
 * Returns null if turn-in cannot be detected (non-cornering zone, insufficient data).
 */
/** Normalize an angle difference to [-π, π]. */
function normalizeAngle(dh: number): number {
  while (dh > Math.PI) dh -= 2 * Math.PI
  while (dh < -Math.PI) dh += 2 * Math.PI
  return dh
}

export function findTurnInDistance(
  refGpsPoints: {
    latitude: number
    longitude: number
    distanceMeters: number
  }[],
  zoneStartDistance: number
): number | null {
  if (refGpsPoints.length < 10) return null

  // Search window: from 80m before zone start to 10m into the zone.
  // Turn-in typically happens 10-40m before the zone boundary.
  const searchBackM = 80
  const searchIntoM = 10
  const searchStart = zoneStartDistance - searchBackM
  const searchEnd = zoneStartDistance + searchIntoM

  // Collect GPS points in the search window
  const windowPts = refGpsPoints.filter(
    (p) => p.distanceMeters >= searchStart && p.distanceMeters <= searchEnd
  )

  if (windowPts.length < 5) return null

  // Compute heading rate of change (curvature proxy) at each point.
  // Use 3-point heading: heading from p[i-1]→p[i] vs p[i]→p[i+1].
  const curvatures: { dist: number; rate: number }[] = []

  for (let i = 1; i < windowPts.length - 1; i++) {
    const h1 = Math.atan2(
      windowPts[i].latitude - windowPts[i - 1].latitude,
      windowPts[i].longitude - windowPts[i - 1].longitude
    )
    const h2 = Math.atan2(
      windowPts[i + 1].latitude - windowPts[i].latitude,
      windowPts[i + 1].longitude - windowPts[i].longitude
    )
    const dh = normalizeAngle(h2 - h1)

    const dDist =
      windowPts[i + 1].distanceMeters - windowPts[i - 1].distanceMeters
    if (dDist > 0) {
      curvatures.push({
        dist: windowPts[i].distanceMeters,
        rate: Math.abs(dh) / dDist,
      })
    }
  }

  if (curvatures.length < 3) return null

  // Determine baseline curvature from the straight-line approach.
  // Use the first third of samples (furthest from corner) as baseline.
  const baselineCount = Math.max(2, Math.floor(curvatures.length / 3))
  const baselineRates = curvatures
    .slice(0, baselineCount)
    .map((c) => c.rate)
    .sort((a, b) => a - b)
  const baselineMedian = baselineRates[Math.floor(baselineRates.length / 2)]

  // Turn-in threshold: 3× baseline or a minimum absolute value.
  // The minimum prevents false positives on perfectly straight approaches.
  const threshold = Math.max(baselineMedian * 3, 0.0005)

  // Walk forward through curvatures to find the first sustained heading
  // change above threshold (require 2 consecutive points to filter noise).
  for (let i = 0; i < curvatures.length - 1; i++) {
    if (curvatures[i].rate > threshold && curvatures[i + 1].rate > threshold) {
      return curvatures[i].dist
    }
  }

  return null
}

/**
 * For cornering zones, find the turn-in point and use it as the display start.
 *
 * This ensures:
 * 1. Lines start at a physically meaningful point (turn-in, not brake point)
 * 2. Entry speed differences between laps are visible
 * 3. All laps share the same gate (the reference lap's turn-in location)
 */
interface FindCorneringDisplayStartArgs {
  domainLaps: Lap[]
  zone: TrackZone
  excludedLapIds: Set<string>
  allZones: TrackZone[]
  refinedBounds: { startDistance: number; endDistance: number }
}

export function findCorneringDisplayStart({
  domainLaps,
  zone,
  excludedLapIds,
  allZones,
  refinedBounds,
}: FindCorneringDisplayStartArgs): {
  startDistance: number
  endDistance: number
} {
  if (zone.type !== 'cornering' || allZones.length === 0) return refinedBounds

  const filteredLaps = domainLaps.filter((lap) => !excludedLapIds.has(lap.id))
  if (filteredLaps.length === 0) return refinedBounds

  const reference =
    filteredLaps.find((lap) => lap.isReliable) ?? filteredLaps[0]
  const refGps = reference.getGpsPoints()
  const turnInDist = findTurnInDistance(refGps, refinedBounds.startDistance)
  if (turnInDist == null) return refinedBounds

  const clampedStart = Math.max(
    refinedBounds.startDistance - 80,
    Math.min(turnInDist, refinedBounds.startDistance)
  )

  return {
    startDistance: clampedStart,
    endDistance: refinedBounds.endDistance,
  }
}

/**
 * Compose the mobile chart-display window: refine zone boundaries, then (for
 * cornering) move the start back to the detected turn-in. This is the exact
 * composition mobile's useZoneTelemetry uses for `chartDisplayBounds`.
 */
export function computeZoneDisplayBounds(
  domainLaps: Lap[],
  zone: TrackZone,
  excludedLapIds: Set<string>,
  allZones: TrackZone[]
): { startDistance: number; endDistance: number } {
  const refinedBounds = refineZoneBoundaries(
    domainLaps,
    zone,
    excludedLapIds,
    allZones
  )
  return findCorneringDisplayStart({
    domainLaps,
    zone,
    excludedLapIds,
    allZones,
    refinedBounds,
  })
}
